import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import AssessmentIcon from "@mui/icons-material/Assessment";
import DrawIcon from "@mui/icons-material/Draw";
import PostAddIcon from "@mui/icons-material/PostAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AutorizacaoStatusChip from "../../components/AutorizacaoStatusChip";
import ProtocoloSecretarioModal from "../../components/ProtocoloSecretarioModal";
import StatusChip from "../../components/StatusChip";
import autorizacoesService from "../../services/autorizacoes";
import isencaoLicenciamentoConsultaService from "../../services/isencaoLicenciamentoConsulta";
import licencaEmitidaService from "../../services/licencaEmitida";
import licencasService from "../../services/licencas";
import protocoloService from "../../services/protocolo";
import { statusIsencaoEnum, statusIsencaoLabels, statusLabelsEnum, tipoAutorizacaoEnum } from "../../utils/constants";
import { protocoloStatusLabels, getAtividadeFlorestalResumo } from "../../utils/protocoloConstants";
import {
  getIsencaoPaletteKey,
  getMuiChipColor,
  getProtocoloPaletteKey,
} from "../../utils/statusPalette";
import { formatDateToShow } from "../../utils/formatters";

const dashboardTableSx = {
  minWidth: 1100,
  width: "100%",
  "& .MuiTableCell-root": {
    py: 1.75,
    px: 2.5,
  },
};

const statusTableCellSx = {
  minWidth: 190,
  width: 190,
};

const SecretarioDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    aguardandoAprovacao: 0,
    aprovadas: 0,
    rejeitadas: 0,
    licencaEmitida: 0,
  });
  const [processos, setProcessos] = useState([]);
  const [recentProcessos, setRecentProcessos] = useState([]);
  const [user] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState("");
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const [autorizacoes, setAutorizacoes] = useState([]);
  const [totalAutorizacoes, setTotalAutorizacoes] = useState(0);
  const [loadingAutorizacoes, setLoadingAutorizacoes] = useState(true);
  const [pageAuth, setPageAuth] = useState(0);
  const [rowsPerPageAuth, setRowsPerPageAuth] = useState(5);

  const [protocolos, setProtocolos] = useState([]);
  const [loadingProtocolos, setLoadingProtocolos] = useState(false);
  const [pageProtocolo, setPageProtocolo] = useState(0);
  const [rowsPerPageProtocolo, setRowsPerPageProtocolo] = useState(5);
  const [totalProtocolos, setTotalProtocolos] = useState(0);
  const [protocoloSelecionado, setProtocoloSelecionado] = useState(null);
  const [protocoloModalAberto, setProtocoloModalAberto] = useState(false);

  const [isencoes, setIsencoes] = useState([]);
  const [loadingIsencoes, setLoadingIsencoes] = useState(false);
  const [pageIsencao, setPageIsencao] = useState(0);
  const [rowsPerPageIsencao, setRowsPerPageIsencao] = useState(5);
  const [totalIsencoes, setTotalIsencoes] = useState(0);

  const carregarProtocolos = async (pag = 0, rpp = 5) => {
    try {
      setLoadingProtocolos(true);
      const result = await protocoloService.listarTodasSecretario(pag + 1, rpp);
      setProtocolos(result?.data ?? []);
      setTotalProtocolos(result?.totalCount ?? 0);
    } catch (error) {
      console.error("Erro ao carregar protocolos:", error);
      setProtocolos([]);
      setTotalProtocolos(0);
    } finally {
      setLoadingProtocolos(false);
    }
  };

  const handleChangePageProtocolo = (event, newPage) => {
    setPageProtocolo(newPage);
  };

  const handleChangeRowsPerPageProtocolo = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageProtocolo(rpp);
    setPageProtocolo(0);
  };

  const carregarAutorizacoes = async (pag = 0, rpp = 5) => {
    try {
      setLoadingAutorizacoes(true);
      const result = await autorizacoesService.listarTodasSecretario({
        pageNumber: pag + 1,
        pageSize: rpp,
      });
      setAutorizacoes(result.items);
      setTotalAutorizacoes(result.totalCount);
    } catch (error) {
      console.error("Erro ao carregar autorizações:", error);
    } finally {
      setLoadingAutorizacoes(false);
    }
  };

  const handleChangePageAuth = (event, newPage) => {
    setPageAuth(newPage);
    carregarAutorizacoes(newPage, rowsPerPageAuth);
  };

  const handleChangeRowsPerPageAuth = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageAuth(rpp);
    setPageAuth(0);
    carregarAutorizacoes(0, rpp);
  };

  const carregarIsencoes = async (pag = 0, rpp = 5) => {
    try {
      setLoadingIsencoes(true);
      const result = await isencaoLicenciamentoConsultaService.listarTodasSecretario({
        pageNumber: pag + 1,
        pageSize: rpp,
      });
      setIsencoes(result?.data?.data ?? []);
      setTotalIsencoes(result?.data?.totalCount ?? 0);
    } catch {
      setIsencoes([]);
      setTotalIsencoes(0);
    } finally {
      setLoadingIsencoes(false);
    }
  };

  const handleChangePageIsencao = (event, newPage) => {
    setPageIsencao(newPage);
  };

  const handleChangeRowsPerPageIsencao = (event) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPageIsencao(rpp);
    setPageIsencao(0);
  };

  const handleVisualizarIsencaoEmitida = async (isencaoId) => {
    try {
      const data = await isencaoLicenciamentoConsultaService.baixarDocumentoEmitido(isencaoId);
      const byteArray = Uint8Array.from(atob(data.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      alert("Erro ao visualizar a isenção. Tente novamente.");
    }
  };

  const carregarProcessos = async () => {
    try {
      setLoading(true);
      const response = await licencasService.listarSolicitacoesSecretario();

      const calculatedStats = {
        total: response.length,
        aguardandoAprovacao: response.filter(
          (l) => l.status === statusLabelsEnum.AGUARDANDO_APROVACAO,
        ).length,
        aprovadas: response.filter(
          (l) => l.status === statusLabelsEnum.APROVADA,
        ).length,
        rejeitadas: response.filter(
          (l) => l.status === statusLabelsEnum.REJEITADA,
        ).length,
        licencaEmitida: response.filter(
          (l) => l.status === statusLabelsEnum.LICENCA_EMITIDA,
        ).length,
      };
      setStats(calculatedStats);

      const sortedProcessos = [...response].sort(
        (a, b) => new Date(b.createAt) - new Date(a.createAt),
      );
      setProcessos(sortedProcessos);
      setRecentProcessos(sortedProcessos);
    } catch (error) {
      console.error("Erro ao carregar processos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!protocoloModalAberto) {
      carregarProcessos();
      carregarAutorizacoes();
      carregarProtocolos(pageProtocolo, rowsPerPageProtocolo);
      carregarIsencoes(pageIsencao, rowsPerPageIsencao);
    }
  }, [protocoloModalAberto, pageProtocolo, rowsPerPageProtocolo, pageIsencao, rowsPerPageIsencao]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAbrirDetalheSolicitacao = (processo) => {
    navigate(`/secretario/solicitacao/${processo.id}`);
  };

  const handleVisualizarLicencaAssinada = async (solicitacaoId) => {
    try {
      const response =
        await licencaEmitidaService.obterPorSolicitacaoDeLicencaId(
          solicitacaoId,
        );
      const licencaData = response.data;

      let blob;
      if (typeof licencaData.fileContent === "string") {
        const byteCharacters = atob(licencaData.fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        blob = new Blob([new Uint8Array(byteNumbers)], {
          type: licencaData.contextType || "application/pdf",
        });
      } else {
        blob = new Blob([new Uint8Array(licencaData.fileContent)], {
          type: licencaData.contextType || "application/pdf",
        });
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      alert("Erro ao carregar a licença assinada.");
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Carregando seu painel de secretário...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        mb={{ xs: 3, md: 4 }}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" } }}
          >
            Painel do Secretário - {user?.username || "Secretário"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gerencie as aprovações de licenciamento ambiental
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={carregarProcessos}
          fullWidth
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Atualizar
        </Button>
      </Box>

      <Box mb={4}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Resumo de Aprovações</Typography>
            <AssessmentIcon color="secondary" />
          </Box>
          <Box mt={3}>
            <Typography variant="body1" gutterBottom>
              Distribuição de status das solicitações ({stats.total} total)
            </Typography>
            <Grid container spacing={3} mt={1}>
              <Grid item xs={12} md={6}>
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">
                      Aguardando Aprovação
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.aguardandoAprovacao} (
                      {stats.total
                        ? Math.round(
                            (stats.aguardandoAprovacao / stats.total) * 100,
                          )
                        : 0}
                      %)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      stats.total
                        ? (stats.aguardandoAprovacao / stats.total) * 100
                        : 0
                    }
                    color="secondary"
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mt: 0.5,
                    }}
                  />
                </Box>
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Aprovadas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.aprovadas} (
                      {stats.total
                        ? Math.round((stats.aprovadas / stats.total) * 100)
                        : 0}
                      %)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      stats.total ? (stats.aprovadas / stats.total) * 100 : 0
                    }
                    color="success"
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Rejeitadas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.rejeitadas} (
                      {stats.total
                        ? Math.round((stats.rejeitadas / stats.total) * 100)
                        : 0}
                      %)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      stats.total ? (stats.rejeitadas / stats.total) * 100 : 0
                    }
                    color="error"
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mt: 0.5,
                    }}
                  />
                </Box>
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Licença Emitida</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.licencaEmitida} (
                      {stats.total
                        ? Math.round(
                            (stats.licencaEmitida / stats.total) * 100,
                          )
                        : 0}
                      %)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      stats.total
                        ? (stats.licencaEmitida / stats.total) * 100
                        : 0
                    }
                    color="info"
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>

      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Solicitações de Licença</Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={dashboardTableSx}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="center">Solicitante</TableCell>
                  <TableCell align="center">Data Inicial</TableCell>
                  <TableCell align="center" sx={statusTableCellSx}>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentProcessos
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((processo) => (
                    <TableRow
                      key={processo.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <TableCell>
                        {processo.nomeTipoLicenca || "Não especificado"}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              mr: 1,
                              bgcolor: "secondary.main",
                              fontSize: "0.8rem",
                            }}
                          >
                            {(() => {
                              const primeiraLetra =
                                processo.solicitante_info?.first_name?.[0]?.toUpperCase() ||
                                processo.licenca?.usuario?.first_name?.[0]?.toUpperCase() ||
                                processo.usuario?.first_name?.[0]?.toUpperCase() ||
                                processo.solicitante_info?.username?.[0]?.toUpperCase() ||
                                processo.licenca?.usuario?.username?.[0]?.toUpperCase() ||
                                processo.usuario?.username?.[0]?.toUpperCase() ||
                                "U";
                              return primeiraLetra;
                            })()}
                          </Avatar>
                          <Typography variant="body2">
                            {processo.solicitanteNome || "Não especificado"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDateToShow(processo.createAt)}
                      </TableCell>
                      <TableCell align="center" sx={statusTableCellSx}>
                        <StatusChip status={processo.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          {processo.status === statusLabelsEnum.LICENCA_EMITIDA && (
                            <>
                              <Tooltip title="Assinar Licença">
                                <IconButton
                                  size="small"
                                  color="success"
                                  aria-label="Assinar Licença"
                                  onClick={() =>
                                    navigate(
                                      `/solicitacao/${processo.id}/assinar`,
                                    )
                                  }
                                >
                                  <DrawIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {processo.status ===
                            statusLabelsEnum.LICENCA_ASSINADA && (
                            <Tooltip title="Visualizar Licença Assinada">
                              <IconButton
                                size="small"
                                color="info"
                                aria-label="Visualizar Licença Assinada"
                                onClick={() =>
                                  handleVisualizarLicencaAssinada(processo.id)
                                }
                              >
                                <VerifiedIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Visualizar Detalhes">
                            <IconButton
                              size="small"
                              color="secondary"
                              aria-label="Visualizar Detalhes"
                              onClick={() => handleAbrirDetalheSolicitacao(processo)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                {recentProcessos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum processo encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={processos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>
      </Box>

      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Protocolos Florestais</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => carregarProtocolos(pageProtocolo, rowsPerPageProtocolo)}
          >
            Atualizar
          </Button>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={dashboardTableSx}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell align="center">Nº Processo</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="center">Atividade</TableCell>
                  <TableCell align="center">Data</TableCell>
                  <TableCell align="center" sx={statusTableCellSx}>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingProtocolos ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : protocolos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhum protocolo aguardando aprovação
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  protocolos.map((protocolo) => (
                    <TableRow key={protocolo.id} hover>
                      <TableCell align="center">{protocolo.numeroProcesso || "—"}</TableCell>
                      <TableCell align="center">{protocolo.processoNome || "—"}</TableCell>
                      <TableCell align="center">
                        {getAtividadeFlorestalResumo(protocolo.atividadeEmpreendimento)}
                      </TableCell>
                      <TableCell align="center">{formatDateToShow(protocolo.createAt)}</TableCell>
                      <TableCell align="center" sx={statusTableCellSx}>
                        <Chip
                          label={protocoloStatusLabels[protocolo.status] ?? protocolo.status}
                          size="small"
                          color={getMuiChipColor(getProtocoloPaletteKey(protocolo.status))}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Visualizar e aprovar">
                          <IconButton
                            size="small"
                            color="secondary"
                            aria-label="Visualizar e aprovar"
                            onClick={() => {
                              setProtocoloSelecionado(protocolo);
                              setProtocoloModalAberto(true);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalProtocolos}
            rowsPerPage={rowsPerPageProtocolo}
            page={pageProtocolo}
            onPageChange={handleChangePageProtocolo}
            onRowsPerPageChange={handleChangeRowsPerPageProtocolo}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>
      </Box>

      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Autorizações</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<PostAddIcon />}
            onClick={() => navigate("/analista/autorizacao-independente")}
          >
            Nova Autorização Independente
          </Button>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={dashboardTableSx}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="center">Nome do Evento</TableCell>
                  <TableCell align="center">Data do Evento</TableCell>
                  <TableCell align="center">Local</TableCell>
                  <TableCell align="center" sx={statusTableCellSx}>Status</TableCell>
                  <TableCell align="center">Data da Solicitação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingAutorizacoes ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : autorizacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma autorização encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  autorizacoes.map((aut) => (
                    <TableRow
                      key={aut.id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <TableCell align="center">
                        {tipoAutorizacaoEnum.find((t) => t.value === aut.tipoAutorizacao)?.label ?? "—"}
                      </TableCell>
                      <TableCell>{aut.nomeEvento}</TableCell>
                      <TableCell align="center">
                        {formatDateToShow(aut.dataEvento)}
                      </TableCell>
                      <TableCell>{aut.localEvento}</TableCell>
                      <TableCell align="center" sx={statusTableCellSx}>
                        <AutorizacaoStatusChip status={aut.status} />
                      </TableCell>
                      <TableCell align="center">
                        {formatDateToShow(aut.createAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="Ver Detalhes"
                            onClick={() => navigate(`/autorizacao/${aut.id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalAutorizacoes}
            rowsPerPage={rowsPerPageAuth}
            page={pageAuth}
            onPageChange={handleChangePageAuth}
            onRowsPerPageChange={handleChangeRowsPerPageAuth}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>
      </Box>

      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Isenções</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => carregarIsencoes(pageIsencao, rowsPerPageIsencao)}
          >
            Atualizar
          </Button>
        </Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Empreendimento</TableCell>
                  <TableCell align="center">Data Solicitação</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingIsencoes ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : isencoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma isenção encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  isencoes.map((isencao) => (
                    <TableRow key={isencao.id} hover>
                      <TableCell>{isencao.empreendimentoNome || "—"}</TableCell>
                      <TableCell align="center">
                        {formatDateToShow(isencao.createAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusIsencaoLabels[isencao.status] ?? isencao.status}
                          size="small"
                          color={getMuiChipColor(getIsencaoPaletteKey(isencao.status))}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          {isencao.status ===
                            statusIsencaoEnum.AGUARDANDO_ASSINATURA && (
                            <Tooltip title="Assinar Isenção">
                              <IconButton
                                size="small"
                                color="success"
                                aria-label="Assinar Isenção"
                                onClick={() =>
                                  navigate(`/isencao/${isencao.id}/assinar`)
                                }
                              >
                                <DrawIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(isencao.status ===
                            statusIsencaoEnum.AGUARDANDO_ASSINATURA ||
                            isencao.status === statusIsencaoEnum.ASSINADA ||
                            !!isencao.isencaoEmitidaS3Key) && (
                            <Tooltip title="Visualizar Documento">
                              <IconButton
                                size="small"
                                color="info"
                                aria-label="Visualizar Documento"
                                onClick={() =>
                                  handleVisualizarIsencaoEmitida(isencao.id)
                                }
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalIsencoes}
            rowsPerPage={rowsPerPageIsencao}
            page={pageIsencao}
            onPageChange={handleChangePageIsencao}
            onRowsPerPageChange={handleChangeRowsPerPageIsencao}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Paper>
      </Box>

      <ProtocoloSecretarioModal
        open={protocoloModalAberto}
        onClose={() => setProtocoloModalAberto(false)}
        protocolo={protocoloSelecionado}
        onAtualizado={() => carregarProtocolos(pageProtocolo, rowsPerPageProtocolo)}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default SecretarioDashboard;
